/**
 * The request shapes of the SystemConfig admin API: list query, path variables,
 * the upsert body and the history page. `../zsystem-config.ts` assembles them
 * into the `ZSystemConfig` contract.
 */

import { z } from "zod";

import { CONFIG_DESCRIPTION_MAX_LENGTH } from "./cron-config.schema";
import { ConfigCategorySchema } from "./system-config.schema";

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * List Configs Query Schema
 */
export const ListConfigsQuerySchema = z.object({
  category: ConfigCategorySchema.optional(),
  // Custom boolean parser for query params (z.coerce.boolean() treats "false" as true!)
  enabled: z
    .union([z.boolean(), z.string()])
    .transform((val) => {
      if (typeof val === "boolean") return val;
      if (val === "true") return true;
      if (val === "false") return false;
      return undefined;
    })
    .optional(),
  search: z.string().max(255).optional()
});

export type ListConfigsQuery = z.infer<typeof ListConfigsQuerySchema>;

/**
 * Get Config Path Variables
 */
export const GetConfigPathVarsSchema = z.object({
  category: ConfigCategorySchema,
  key: z.string().min(1).max(255)
});

export type GetConfigPathVars = z.infer<typeof GetConfigPathVarsSchema>;

/**
 * Upsert Config Body Schema
 *
 * Creates or updates a config entry.
 */
export const UpsertConfigBodySchema = z.object({
  config: z.record(z.string(), z.unknown()),

  /**
   * The row's `description` column: the admin's own note, for every category
   * (`CONFIG_DESCRIPTION_COLUMN_OWNERSHIP`) — the code's description is served
   * apart, as `codeDefault`. It carries the SAME cap as the code's description
   * (`CronConfigSchema.description`), so a row still holding prose an earlier
   * sync wrote round-trips through an admin save without a 400 on a save that
   * changed nothing. A blank value is stored as no note (`null`).
   */
  description: z.string().max(CONFIG_DESCRIPTION_MAX_LENGTH).optional(),
  enabled: z.boolean().optional(),

  /**
   * Deliberately NOT the description cap. This is the human's note about why
   * they made the change, not the config's own prose, and nothing writes it to
   * two schemas — so it has no reason to move when that one does.
   */
  changeReason: z.string().max(500).optional()
});

export type UpsertConfigBody = z.infer<typeof UpsertConfigBodySchema>;

/**
 * Delete Config Path Variables
 */
export const DeleteConfigPathVarsSchema = z.object({
  category: ConfigCategorySchema,
  key: z.string().min(1).max(255)
});

export type DeleteConfigPathVars = z.infer<typeof DeleteConfigPathVarsSchema>;

/**
 * Delete Config By Id Path Variables — the row's id, whatever its category.
 */
export const DeleteConfigByIdPathVarsSchema = z.object({
  id: z.string().uuid()
});

export type DeleteConfigByIdPathVars = z.infer<typeof DeleteConfigByIdPathVarsSchema>;

/**
 * Reset Config Path Variables
 */
export const ResetConfigPathVarsSchema = z.object({
  category: ConfigCategorySchema,
  key: z.string().min(1).max(255)
});

export type ResetConfigPathVars = z.infer<typeof ResetConfigPathVarsSchema>;

/**
 * Get History Path Variables
 */
export const GetHistoryPathVarsSchema = z.object({
  category: ConfigCategorySchema,
  key: z.string().min(1).max(255)
});

export type GetHistoryPathVars = z.infer<typeof GetHistoryPathVarsSchema>;

/**
 * Get History Query Schema
 */
export const GetHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional()
});

export type GetHistoryQuery = z.infer<typeof GetHistoryQuerySchema>;
